import { Injectable, inject } from '@angular/core';
import type { BackendAllErrorCodes } from '@freelancer/datastore';
import type { BackendErrorResponse } from '@freelancer/freelancer-http';
import { isImageFile } from '@freelancer/ui';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import type { Observable } from 'rxjs';
import { BehaviorSubject, of, fromEvent, firstValueFrom } from 'rxjs';
import { catchError, shareReplay, map, last } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { configs } from './file-storage-backend.config';
import type {
  FullMetadata,
  SettableMetadata,
  StorageBucket,
  UploadTaskEvent,
} from './file-storage.model';
import type { UploadTask } from './file-storage.service';
import { FileStorage } from './file-storage.service';

export interface FileAttachment {
  id: string;
  file: File;
  // undefined value is for non-image files
  filePreviewUrl$: Observable<string | undefined>;
  metadata?: SettableMetadata;
}

export interface FileUpload extends FileAttachment {
  uploadTask: UploadTask;
  uploadTaskEvent$: Observable<UploadTaskEvent>;
  uploadPercentage$: Observable<number>;
  persistFailed: boolean;
  isLegacyUpload: boolean;
  /**
   * The upload finished but nothing has claimed the file yet, so it is
   * parked in the composer rather than in flight.
   */
  isAwaitingSend: boolean;
}

export interface BatchUploadFileResult {
  readonly uuid: string;
  readonly name: string;
  readonly fileUpload: FileUpload;
}

export type BatchUploadResult =
  | { readonly ok: true; readonly files: readonly BatchUploadFileResult[] }
  | {
      readonly ok: false;
      readonly failed: readonly FileUpload[];
      readonly succeeded: readonly BatchUploadFileResult[];
    };

export type NewFileServiceFlowCallback = (
  fileUpload: FileUpload,
  metadata: FullMetadata,
) => void;

/**
 * Errors the backend will raise again for the same bytes and filename, so
 * re-uploading is pointless and the file has to be dropped instead.
 *
 * The remaining `ATTACHMENT_FILE_*` codes are deliberately not here: they are
 * request-shape or lookup failures (`*_REQUIRED`, `*_ID_REQUIRED`,
 * `*_METADATA_REQUIRED`, `*_NOT_FOUND`, `*_UUID_DUPLICATE`) rather than
 * properties of the file, so a retry can legitimately succeed.
 */
const TERMINAL_UPLOAD_ERROR_CODES: readonly string[] = [
  ErrorCodeApi.ATTACHMENT_FILE_UNSUPPORTED_MIME_TYPE,
  ErrorCodeApi.ATTACHMENT_FILE_TOO_LARGE,
  ErrorCodeApi.ATTACHMENT_FILE_FILENAME_INVALID,
  ErrorCodeApi.ATTACHMENT_FILE_FILENAME_TOO_LONG,
];

export function isTerminalUploadError(errorCode: string): boolean {
  return TERMINAL_UPLOAD_ERROR_CODES.includes(errorCode);
}

type ClassifiedUpload =
  | { readonly kind: 'done'; readonly result: BatchUploadFileResult }
  | { readonly kind: 'failed'; readonly fileUpload: FileUpload }
  | { readonly kind: 'pending'; readonly fileUpload: FileUpload };

/**
 * The class for managing file attachment and file uploads
 * using the FileStorage service as the backend upload service.
 */
export class FileUploadSession {
  // The list of files waiting to be uploaded.
  private fileAttachments: readonly FileAttachment[] = [];
  private fileAttachmentsSubject$ = new BehaviorSubject<
    readonly FileAttachment[]
  >([]);
  private fileAttachments$ = this.fileAttachmentsSubject$.asObservable();

  // The list of files that have started uploading.
  private fileUploads: readonly FileUpload[] = [];
  private fileUploadsSubject$ = new BehaviorSubject<readonly FileUpload[]>([]);
  private fileUploads$ = this.fileUploadsSubject$.asObservable();

  private isLegacyUpload = false;

  constructor(
    private fileStorage: FileStorage,
    private bucket: StorageBucket,
  ) {
    this.isLegacyUpload = this.isBucketUsingLegacyUpload(bucket);
  }

  /**
   * This function adds the file in the list of file attachments to be uploaded / sent.
   */
  addFile(file: File, metadata?: SettableMetadata): void {
    if (!file || this.isFileExists(file, this.fileAttachments)) {
      return;
    }

    // Create an observable of preview URL for image file
    // while empty URL for non-image file
    const filePreviewUrl$ = isImageFile(file.name)
      ? this.readFileAsDataURL(file).pipe(
          shareReplay({ bufferSize: 1, refCount: true }),
        )
      : of(undefined);

    this.setFileAttachments(
      this.add<FileAttachment>(
        {
          id: uuidv4(),
          file: this.fixConflictFilenameFile(file, this.fileAttachments),
          filePreviewUrl$,
          metadata,
        },
        this.fileAttachments,
      ),
    );
  }

  /**
   * This function removes the file in the list of file attachments.
   */
  removeFile(fileAttachment: FileAttachment): void {
    this.setFileAttachments(
      this.remove<FileAttachment>(fileAttachment.id, this.fileAttachments),
    );
  }

  /**
   * This function removes the file in the file upload list given the id of the
   * uploading file. The function will first cancel any running uploading file
   * before removing it from the uploading file list.
   */
  async removeFileUpload(fileUpload: FileUpload): Promise<void> {
    // Make sure uploading file is cancelled before removing in the uploading list
    await firstValueFrom(fileUpload.uploadTaskEvent$).then(uploadTaskEvent => {
      if (uploadTaskEvent.state === 'running') {
        fileUpload.uploadTask.cancel();
      }
    });

    this.setFileUploads(
      this.remove<FileUpload>(fileUpload.id, this.fileUploads),
    );
  }

  /**
   * This function marks the file upload persist as failed and is only used in the
   * new file service flow. The new file service flow uploads the file temporarily which
   * needs to be marked as persist in the backend.
   */
  markFileUploadPersistAsFailed(
    fileUpload: FileUpload,
    error: BackendErrorResponse<BackendAllErrorCodes>,
  ): void {
    const updatedFileUpload = {
      ...fileUpload,
      ...{
        uploadTaskEvent$: fileUpload.uploadTaskEvent$.pipe(
          map(event => {
            return {
              ...event,
              ...{
                state: error.status,
                error: {
                  ...error,
                },
              },
            } as UploadTaskEvent;
          }),
        ),
      },
      persistFailed: true,
      isAwaitingSend: false,
    };

    this.updateFileUpload(updatedFileUpload);
  }

  /**
   * Returns an observable of list of file attachments that are ready to be uploaded.
   */
  getFileAttachments(): Observable<readonly FileAttachment[]> {
    return this.fileAttachments$;
  }

  /**
   * Returns an observable of list of uploading files.
   */
  getFileUploads(): Observable<readonly FileUpload[]> {
    return this.fileUploads$;
  }

  /**
   * This function updates the metadata of the passed file attachment.
   */
  updateFileAttachmentMetadata(
    fileAttachment: FileAttachment,
    metadata: SettableMetadata,
  ): void {
    const updatedFileAttachment = {
      ...fileAttachment,
      metadata,
    };

    this.updateFileAttachment(updatedFileAttachment);
  }

  /**
   * Clear all the files in the list of file attachments.
   */
  clearAllFileAttachments(): void {
    this.setFileAttachments([]);
  }

  /**
   * This function retries the given file upload.
   * If the file storage bucket is using the new file service flow, the callback
   * persists each file as it completes. Omit it to only re-upload, for callers
   * that persist a whole batch themselves once `sendAll` resolves.
   */
  async retryFileUpload(
    fileUpload: FileUpload,
    newFileServiceFlowCallback?: NewFileServiceFlowCallback,
  ): Promise<FileUpload> {
    if (this.isLegacyUpload && !!newFileServiceFlowCallback) {
      throw new Error(
        'Callback function should only be passed for new file service flow',
      );
    }

    // Make sure that the uploading of file is already cancelled
    await firstValueFrom(fileUpload.uploadTaskEvent$).then(uploadTaskEvent => {
      if (uploadTaskEvent.state === 'running') {
        fileUpload.uploadTask.cancel();
      }
    });

    const uploadTask = this.fileStorage.upload(
      this.bucket,
      fileUpload.file,
      fileUpload.metadata,
    );

    // Only update the file upload to retain the order and id
    const updatedFileUpload: FileUpload = {
      ...fileUpload,
      uploadTask,
      uploadTaskEvent$: uploadTask.changes(),
      uploadPercentage$: uploadTask.percentageChanges(),
      persistFailed: false,
      isAwaitingSend: false,
      isLegacyUpload: this.isLegacyUpload,
    };

    this.updateFileUpload(updatedFileUpload);

    if (!this.isLegacyUpload && !!newFileServiceFlowCallback) {
      this.processNewFileServiceFlow(
        updatedFileUpload,
        newFileServiceFlowCallback,
      );
    }

    return updatedFileUpload;
  }

  /**
   * This function will trigger the uploading of all the files in the list of file attachments,
   * and move each uploading files to the list of uploading files. The callback is only used for
   * the new file service flow, the parameter should not be passed when using the legacy flow.
   */
  send(newFileServiceFlowCallback?: NewFileServiceFlowCallback): void {
    if (!this.isLegacyUpload && !newFileServiceFlowCallback) {
      throw new Error(
        'Callback function is required for uploading file in new file service flow',
      );
    }

    if (this.isLegacyUpload && !!newFileServiceFlowCallback) {
      throw new Error(
        'Callback function should only be passed for new file service flow',
      );
    }

    firstValueFrom(this.fileAttachments$).then(fileAttachments => {
      fileAttachments.forEach(fileAttachment => {
        this.uploadFileAttachment(fileAttachment, newFileServiceFlowCallback);
      });
    });
  }

  /**
   * Uploads every pending attachment and re-uploads anything that no longer has
   * a usable file id, so that the caller can attach the whole batch to a single
   * record. Does not persist anything itself.
   *
   * Files added after this is called are left for the next call. Removing a
   * file while it is uploading drops it from the batch rather than failing it.
   */
  async sendAll(): Promise<BatchUploadResult> {
    const [attachments, existingUploads] = await Promise.all([
      firstValueFrom(this.fileAttachments$),
      firstValueFrom(this.fileUploads$),
    ]);

    const classified = await Promise.all(
      existingUploads.map(upload => this.classifyExistingUpload(upload)),
    );

    const alreadySucceeded = classified.flatMap(item =>
      item.kind === 'done' ? [item.result] : [],
    );
    const alreadyFailed = classified.flatMap(item =>
      item.kind === 'failed' ? [item.fileUpload] : [],
    );
    const pending = classified.flatMap(item =>
      item.kind === 'pending' ? [item.fileUpload] : [],
    );

    const newlyStarted = attachments.map(attachment =>
      this.startUpload(attachment),
    );

    const terminalResults = await Promise.all(
      [...pending, ...newlyStarted].map(async fileUpload => ({
        fileUpload,
        event: await this.waitForTerminalEvent(fileUpload),
      })),
    );

    const currentUploads = await firstValueFrom(this.fileUploads$);
    const positionById = new Map(
      currentUploads.map((fileUpload, index) => [fileUpload.id, index]),
    );
    const latestById = new Map(
      currentUploads.map(fileUpload => [fileUpload.id, fileUpload]),
    );

    const succeededFromBatch: BatchUploadFileResult[] = [];
    const failedFromBatch: FileUpload[] = [];

    terminalResults.forEach(({ fileUpload, event }) => {
      // Gone from the list means it was removed while uploading, which is a
      // deliberate drop rather than a failure.
      const latest = latestById.get(fileUpload.id);
      if (!latest) {
        return;
      }

      const uuid = this.getUuidFromEvent(event);
      if (event?.state === 'success' && uuid !== undefined) {
        succeededFromBatch.push({
          uuid,
          name: event.metadata.name,
          fileUpload: latest,
        });
        return;
      }

      // Anything still in the list that did not resolve to a file id has to
      // count as failed, otherwise the batch would quietly ship without it.
      failedFromBatch.push(latest);
    });

    const succeeded = this.sortByPosition(
      [...alreadySucceeded, ...succeededFromBatch].filter(result =>
        positionById.has(result.fileUpload.id),
      ),
      positionById,
      result => result.fileUpload.id,
    );
    const failed = this.sortByPosition(
      [...alreadyFailed, ...failedFromBatch].filter(fileUpload =>
        positionById.has(fileUpload.id),
      ),
      positionById,
      fileUpload => fileUpload.id,
    );

    if (failed.length > 0) {
      return { ok: false, failed, succeeded };
    }

    return { ok: true, files: succeeded };
  }

  /**
   * Marks finished uploads as no longer in flight, for when a batch could not
   * be sent and the files stay put.
   */
  markFileUploadsAwaitingSend(fileUploads: readonly FileUpload[]): void {
    const ids = new Set(fileUploads.map(fileUpload => fileUpload.id));
    firstValueFrom(this.fileUploads$).then(currentUploads => {
      currentUploads
        .filter(fileUpload => ids.has(fileUpload.id))
        .forEach(fileUpload => {
          this.updateFileUpload({
            ...fileUpload,
            isAwaitingSend: true,
          });
        });
    });
  }

  /**
   * Restores the order the files appear in for the user, which the batch loses
   * because already-uploaded files resolve ahead of re-uploaded ones.
   */
  private sortByPosition<T>(
    items: readonly T[],
    positionById: ReadonlyMap<string, number>,
    getId: (item: T) => string,
  ): readonly T[] {
    return [...items].sort(
      (a, b) =>
        (positionById.get(getId(a)) ?? 0) - (positionById.get(getId(b)) ?? 0),
    );
  }

  /**
   * NOTE: THIS SHOULD ONLY BE USED WHEN MAKING THE CURRENT BUCKET TO FALLBACK TO THE LEGACY FLOW
   * This function update the storage bucket to be used for the file upload session instance.
   */
  updateFileUploadSessionBucket(bucket: StorageBucket): void {
    this.bucket = bucket;
    this.isLegacyUpload = this.isBucketUsingLegacyUpload(bucket);
  }

  private async uploadFileAttachment(
    fileAttachment: FileAttachment,
    newFileServiceFlowCallback?: NewFileServiceFlowCallback,
  ): Promise<void> {
    const fileUpload = this.startUpload(fileAttachment);

    if (!this.isLegacyUpload && !!newFileServiceFlowCallback) {
      this.processNewFileServiceFlow(fileUpload, newFileServiceFlowCallback);
    }
  }

  private async classifyExistingUpload(
    upload: FileUpload,
  ): Promise<ClassifiedUpload> {
    const latestEvent = await this.getLatestUploadEvent(upload);

    if (latestEvent?.state === 'running') {
      return { kind: 'pending', fileUpload: upload };
    }

    if (this.isTerminalFailure(upload, latestEvent)) {
      return { kind: 'failed', fileUpload: upload };
    }

    if (this.fileUploadNeedsReupload(upload, latestEvent)) {
      return {
        kind: 'pending',
        fileUpload: await this.retryFileUpload(upload),
      };
    }

    // Anything left either succeeded, or failed to persist while its bytes are
    // still good. Both are ready to attach, so the uuid is all that matters.
    const uuid = this.getUuidFromEvent(latestEvent);
    if (uuid !== undefined) {
      return {
        kind: 'done',
        result: {
          uuid,
          name: upload.file.name,
          fileUpload: upload,
        },
      };
    }

    return {
      kind: 'pending',
      fileUpload: await this.retryFileUpload(upload),
    };
  }

  private startUpload(fileAttachment: FileAttachment): FileUpload {
    const uploadTask = this.fileStorage.upload(
      this.bucket,
      fileAttachment.file,
      fileAttachment.metadata,
    );

    const fileUpload: FileUpload = {
      ...fileAttachment,
      uploadTask,
      uploadTaskEvent$: uploadTask.changes(),
      uploadPercentage$: uploadTask.percentageChanges(),
      persistFailed: false,
      isAwaitingSend: false,
      isLegacyUpload: this.isLegacyUpload,
    };

    this.setFileUploads(this.add<FileUpload>(fileUpload, this.fileUploads));
    this.setFileAttachments(
      this.remove<FileAttachment>(fileAttachment.id, this.fileAttachments),
    );

    return fileUpload;
  }

  private async getLatestUploadEvent(
    fileUpload: FileUpload,
  ): Promise<UploadTaskEvent | undefined> {
    try {
      return await firstValueFrom(
        fileUpload.uploadTaskEvent$.pipe(catchError(() => of(undefined))),
      );
    } catch {
      return undefined;
    }
  }

  private async waitForTerminalEvent(
    fileUpload: FileUpload,
  ): Promise<UploadTaskEvent | undefined> {
    try {
      return await firstValueFrom(fileUpload.uploadTaskEvent$.pipe(last()));
    } catch {
      return undefined;
    }
  }

  /**
   * The backend rejected the file itself, so sending the same bytes again would
   * fail the same way. The caller has to drop it or the user has to remove it.
   */
  private isTerminalFailure(
    fileUpload: FileUpload,
    event: UploadTaskEvent | undefined,
  ): boolean {
    return (
      !fileUpload.persistFailed &&
      event?.state === 'error' &&
      isTerminalUploadError(event.error.errorCode)
    );
  }

  private fileUploadNeedsReupload(
    fileUpload: FileUpload,
    event: UploadTaskEvent | undefined,
  ): boolean {
    if (!event) {
      return true;
    }
    if (fileUpload.persistFailed) {
      // The temporary upload expired before it could be attached to anything,
      // so the bytes have to go up again.
      return (
        event.state === 'error' &&
        event.error.errorCode === ErrorCodeApi.ATTACHMENT_FILE_NOT_FOUND
      );
    }
    return event.state === 'error';
  }

  private getUuidFromEvent(
    event: UploadTaskEvent | undefined,
  ): string | undefined {
    const uuid = event?.metadata.id;
    return uuid !== undefined && uuid !== '' ? uuid : undefined;
  }

  private async processNewFileServiceFlow(
    fileUpload: FileUpload,
    newFileServiceFlowCallback: NewFileServiceFlowCallback,
  ): Promise<void> {
    const taskEvent = await firstValueFrom(
      fileUpload.uploadTaskEvent$.pipe(last()),
    );

    if (taskEvent.state === 'success') {
      newFileServiceFlowCallback(fileUpload, taskEvent.metadata);
    }
  }

  /**
   * Read the file as data URL used for previewing of image files
   * while it is not yet uploaded.
   */
  private readFileAsDataURL(file: File): Observable<string> {
    const fileReader = new FileReader();

    fileReader.readAsDataURL(file);

    // We can cast to string here as we are using the `readAsDataURL` function,
    // the result will always be a string.
    return fromEvent(fileReader, 'load').pipe(
      map(() => fileReader.result as string),
    );
  }

  private setFileAttachments(fileAttachments: readonly FileAttachment[]): void {
    this.fileAttachments = fileAttachments;
    this.fileAttachmentsSubject$.next(fileAttachments);
  }

  private setFileUploads(fileUploads: readonly FileUpload[]): void {
    this.fileUploads = fileUploads;
    this.fileUploadsSubject$.next(fileUploads);
  }

  private add<T extends FileAttachment | FileUpload>(
    t: T,
    tList: readonly T[],
  ): readonly T[] {
    return [...tList, t];
  }

  private remove<T extends FileAttachment | FileUpload>(
    tId: string,
    tList: readonly T[],
  ): readonly T[] {
    return tList.filter(t => t.id !== tId);
  }

  private update<T extends FileAttachment | FileUpload>(
    tNew: T,
    tList: readonly T[],
  ): readonly T[] {
    const fileIndex = tList.findIndex(t => t.id === tNew.id);

    if (fileIndex !== -1) {
      // Retain the positioning of the updated file in the list
      return [
        ...tList.slice(0, fileIndex),
        tNew,
        ...tList.slice(fileIndex + 1),
      ];
    }

    return tList;
  }

  /**
   * This function replaces the old file upload with the passed
   * updated file upload while maintaining the order of the file attachment list.
   */
  private updateFileUpload(updatedFileUpload: FileUpload): void {
    this.setFileUploads(
      this.update<FileUpload>(updatedFileUpload, this.fileUploads),
    );
  }

  /**
   * This function replaces the old file attachment with the passed
   * updated file attachment while maintaining the order of the file attachment list.
   */
  private updateFileAttachment(updatedFileAttachment: FileAttachment): void {
    this.setFileAttachments(
      this.update<FileAttachment>(updatedFileAttachment, this.fileAttachments),
    );
  }

  /**
   * Migrated from file-upload-legacy.ts
   *
   * Check if the same file exists in the files array.
   */
  private isFileExists(
    file: File,
    fileAttachments: readonly FileAttachment[],
  ): boolean {
    return (
      !!fileAttachments &&
      fileAttachments.some(
        f => f.file.name === file.name && f.file.size === file.size,
      )
    );
  }

  /**
   * Migrated from file-upload-legacy.ts
   *
   * This function checks for files with conflicting filenames (e.g. 2 test.txt)
   * in the files array.
   *
   * This returns the original file if there is no conflicting name, else it
   * will return a renamed file (e.g. test.txt -> test_1.txt, test_2.txt, etc.)
   */
  private fixConflictFilenameFile(
    file: File,
    fileAttachments: readonly FileAttachment[],
  ): File {
    const filename = file.name.split('.');
    const extensions = filename.slice(1).join('.');
    const matchingFiles = fileAttachments
      .filter(
        f =>
          f.file.name.startsWith(filename[0]) &&
          f.file.name.endsWith(extensions),
      )
      .sort((a, b) => (a.file.name < b.file.name ? -1 : 1));

    // only modify name if there's a match (also runs when there are no matchingFiles)
    if (matchingFiles.every(f => f.file.name !== file.name)) {
      return file;
    }

    const lastMatch = matchingFiles.at(-1);
    if (!lastMatch) {
      return file;
    }
    const matchName = lastMatch.file.name.split('.');
    const fileNumber =
      filename[0] !== matchName[0]
        ? Number(
            matchName[0].substring(
              matchName[0].length - 2,
              matchName[0].length - 2 + 2,
            ),
          ) + 1
        : 1;
    const fileNumberPad = `_${`0${fileNumber}`.slice(-2)}`;
    filename[0] += fileNumberPad;
    return new File([file.slice(0, file.size)], filename.join('.'));
  }

  /**
   * This function checks if the storage bucket is using legacy upload.
   */
  private isBucketUsingLegacyUpload(bucket: StorageBucket): boolean {
    // Random ID to simply get the bucket config
    const randomId = uuidv4();

    return configs[bucket]({
      bucket,
      id: randomId,
    }).isLegacy;
  }
}

/**
 * Factory class that creates a FileUploadSession for managing
 * file attachment and file uploads.
 */
@Injectable({ providedIn: 'root' })
export class FileUploadSessionFactory {
  private fileStorage = inject(FileStorage);

  /**
   * Create an instance of FileUploadSession for the passed storage bucket.
   */
  createSession(bucket: StorageBucket): FileUploadSession {
    return new FileUploadSession(this.fileStorage, bucket);
  }
}
